import { Link } from "@tanstack/react-router";

const STAGES = [
  { key: "peri", label: "Perimenopause", stage: "Stage 01", href: "/perimenopause" },
  { key: "meno", label: "Menopause", stage: "Stage 02", href: "/menopause" },
  { key: "post", label: "Postmenopause", stage: "Stage 03", href: "/postmenopause" },
] as const satisfies ReadonlyArray<{
  readonly key: string;
  readonly label: string;
  readonly stage: string;
  readonly href: "/perimenopause" | "/menopause" | "/postmenopause";
}>;

export type StageKey = (typeof STAGES)[number]["key"];

const TOKENS = {
  sand: "#FFE3E8",
  charcoal: "#1A0A24",
  white: "#FFFFFF",
  muted: "#3F2F4A",
  accent: "#B91C5C",
  accentSoft: "#FBE4EE",
} as const;
const SERIF = "var(--font-display)";

export interface StageNavProps {
  readonly current: StageKey;
}

export function StageNav({ current }: StageNavProps) {

  return (
    <nav aria-label="Menopause stages" className="grid gap-3 sm:grid-cols-3">
      {STAGES.map((s) => {
        const isCurrent = s.key === current;
        return (
          <Link
            key={s.key}
            to={s.href}
            aria-current={isCurrent ? "page" : undefined}
            className="km-lift relative flex items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 transition"
            style={{
              background: isCurrent ? TOKENS.accentSoft : TOKENS.white,
              borderColor: isCurrent ? TOKENS.accent : `${TOKENS.sand}aa`,
            }}
          >
            {isCurrent && (
              <span
                aria-hidden
                className="absolute left-0 top-0 h-full w-1"
                style={{ background: TOKENS.accent }}
              />
            )}
            <span
              className="grid h-9 w-9 flex-none place-items-center rounded-xl"
              style={{
                background: isCurrent ? TOKENS.white : TOKENS.accentSoft,
                color: TOKENS.accent,
              }}
              aria-hidden
            >
              <span className="text-[14px] font-semibold">{s.stage.slice(-2)}</span>
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className="text-[10.5px] font-semibold uppercase tracking-[0.14em]"
                style={{ color: isCurrent ? TOKENS.accent : TOKENS.muted }}
              >
                {s.stage}
              </span>
              <span
                style={{
                  fontFamily: SERIF,
                  fontSize: 17,
                  letterSpacing: "-0.01em",
                  color: isCurrent ? TOKENS.accent : TOKENS.charcoal,
                }}
              >
                {s.label}
              </span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
